<template>
  <div v-if="quote && Object.keys(quote).length">
    <!-- EXPANDED -->
    <v-slide-y-transition hide-on-leave>
      <div v-show="quote.shown">
        <!-- ONLY DESKTOP -->
        <div
          v-if="$vuetify.breakpoint.mdAndUp"
          class="d-flex justify-space-between"
        >
          <!-- HIDE QUOTE -->
          <v-btn
            v-ripple="false"
            small
            color="white"
            class="elevation-1 remove-bottom-radius mx-4"
            @click="$emit('toggle-quote-shown', quote.id)"
          >
            <v-icon
              color="primary"
              :size="16"
              class="mr-1"
              style="margin-bottom: 2px;"
            >
              mdi-minus-circle-outline
            </v-icon>
            <span class="primary--text caption">
              Hide quote
            </span>
          </v-btn>
        </div>

        <v-card :class="{ 'offer-border': hasSpecialOffer }">
          <v-container
            v-if="$vuetify.breakpoint.mdAndUp"
            fluid
          >
            <!-- DESKTOP -->
            <div>
              <v-row class="flex-nowrap">
                <!-- Provider -->
                <v-col
                  class="d-flex flex-column justify-center align-center
                  table-right-border table-right-border-light result-columns-align-width"
                  @click="onClickViewDetails(quote)"
                >
                  <img
                    v-if="qd.imageSiteRemote"
                    :src="qd.imageSiteRemote"
                    :alt="qd.brokerFullName"
                    class="provider-logo"
                  >
                  <div
                    v-else
                    class="font-weight-bold text-center"
                  >
                    {{ qd.brokerFullName }}
                  </div>
                  <results-price
                    v-if="isViewMonthly"
                    :quote="quote"
                    :monthly="!isViewMonthly"
                    :selected-optional-extras="selectedOptionalExtras"
                    class="info--text pt-2"
                  />
                </v-col>
                <!-- Price -->
                <v-col
                  class="d-flex flex-column justify-center align-center
                  table-right-border table-right-border-light result-columns-align-width"
                  @click="onClickViewDetails(quote)"
                >
                  <results-price
                    :quote="quote"
                    :monthly="isViewMonthly"
                    :cashback="cashback"
                    :selected-optional-extras="selectedOptionalExtras"
                    @open-drawer-details="$emit('open-drawer-details', ['details-description', 'estimatedPrice'])"
                  />
                </v-col>

                <!-- Optional extras -->
                <v-col
                  v-for="extra of optionalExtrasColumns"
                  :key="extra"
                  class="d-flex flex-column justify-center align-center
                table-right-border table-right-border-light result-columns-align-width col"
                >
                  <div
                    v-if="extra === 'maxExcess'"
                    class="font-weight-bold headline"
                  >
                    &pound;{{ qd.maxExcess || 0 }}
                  </div>
                  <results-optional-extra
                    v-else
                    :extra="qd.optionalExtra[extra]"
                    :selected="selectedOptionalExtras.includes(extra)"
                  />
                </v-col>

                <!-- Policy details -->
                <v-col
                  class="d-flex flex-column justify-center align-center text-center
                       result-columns-align-width"
                >
                  <v-btn
                    rounded
                    color="success"
                    :disabled="quotesLoading"
                    @click="onClickViewDetails(quote)"
                  >
                    <span>More info</span>
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </v-container>
          <!-- TABLETS/MOBILES -->
          <results-quote-mobile
            v-else
            :quote="quote"
            :is-view-monthly="isViewMonthly"
            :optional-extras-columns="optionalExtrasColumns"
            :selected-optional-extras="selectedOptionalExtras"
            @click-view-details="onClickViewDetails"
          />
          <div
            v-if="hasSpecialOffer"
            class="accent-gradient caption py-2 px-3"
            style="margin-right: 0.5px;"
          >
            <v-icon
              small
              color="navy-dark"
              class="mr-2"
            >
              mdi-tag
            </v-icon>
            <span v-html="qd.companyConfig.featuredText" />
          </div>
          <div
            v-else-if="hasInfoText"
            class="caption py-2 px-3"
            style="margin-right: 0.5px;"
          >
            <v-icon
              small
              color="navy-dark"
              class="mr-2"
            >
              mdi-tag
            </v-icon>
            <span v-html="qd.specialOffer" />
          </div>
        </v-card>
      </div>
    </v-slide-y-transition>

    <!-- COLLAPSED -->
    <v-slide-y-reverse-transition hide-on-leave>
      <v-card
        v-show="!quote.shown"
        class="card"
      >
        <v-container fluid>
          <div class="d-flex align-items-center">
            <v-btn
              v-ripple="false"
              small
              color="white"
              depressed
              @click="$emit('toggle-quote-shown', quote.id)"
            >
              <v-icon
                color="primary"
                :size="16"
                class="mr-1"
                style="margin-bottom: 2px;"
              >
                mdi-plus-circle-outline
              </v-icon>
              <span class="primary--text caption">
                Show hidden quote
              </span>
            </v-btn>

            <img
              :src="qd.imageSiteRemote"
              :alt="qd.brokerFullName"
              class="hidden-quote-img px-4 mx-3"
            >

            <span class="title">
              <!-- eslint-disable-next-line max-len -->
              &pound;{{ qd.calcAnnual.toString().split('.')[0] }}<span class="subtitle-2">.{{ qd.calcAnnual.toString().split('.')[1] }}</span>
              <span class="caption ml-2 mb-0">per year</span>
            </span>
          </div>
        </v-container>
      </v-card>
    </v-slide-y-reverse-transition>
  </div>
</template>

<script>
import ResultsPrice from '@/components/car/Results/ResultsPrice.vue';
import ResultsOptionalExtra from '@/components/travel/Results/ResultsOptionalExtra.vue';
import ResultsQuoteMobile from '@/components/travel/Results/ResultsQuoteMobile';
import { sortQuoteOptions } from '@/helpers/common';

export default {
  name: 'results-quote',
  components: {
    ResultsPrice,
    ResultsOptionalExtra,
    ResultsQuoteMobile,
  },
  props: {
    quotesLoading: {
      type: Boolean,
      default: false,
    },
    quote: {
      type: Object,
      required: true,
    },
    isViewMonthly: {
      type: Boolean,
      required: true,
    },
    optionalExtrasColumns: {
      type: Array,
      required: true,
    },
    selectedOptionalExtras: {
      type: Array,
      required: true,
    },
    coverBoostProducts: {
      type: Array,
      default: undefined,
    },
  },
  computed: {
    qd () {
      return this.quote.details[0];
    },
    hasSpecialOffer () {
      // proper special offers are listed in companyConfig / featuredText
      return !!this.qd.companyConfig && Object.keys(this.qd.companyConfig).length > 0;
    },
    hasInfoText () {
      // general info text (in json response as specialOffer)
      return !!this.qd.specialOffer && this.qd.specialOffer.length > 0;
    },
    mobileExtras () {
      return this.optionalExtrasColumns.filter(this.showOptionalExtra).sort(this.sortMobileExtras);
    },
    cashback () {
      return +this.qd.cashback;
    },
  },
  methods: {
    onClickViewDetails (quote) {
      this.$emit('click-view-details', quote);
    },
    showOptionalExtra (optionalExtra) {
      return optionalExtra === 'maxExcess'
        ? false
        : this.qd.optionalExtra[optionalExtra].included !== 'N';
    },
    selectedOptionalExtra (optionalExtra) {
      return (this.selectedOptionalExtras.includes(optionalExtra) &&
        this.qd.optionalExtra[optionalExtra].cover !== null);
    },
    sortMobileExtras (keyOptionA, keyOptionB) {
      const oA = this.qd.optionalExtra[keyOptionA].included;
      const oB = this.qd.optionalExtra[keyOptionB].included;
      return sortQuoteOptions(oA, oB);
    },
  },
};
</script>

<style scoped lang="scss" src='@/assets/scss/results/results-quote.scss' />
